import { activeStatuses, ec, ecId, HandBack, insertRoll, itemField, record, upper, type JanusCtx, type Row } from '../ability/context.js';
import { applyStatusEffect } from '../ability/statuses/apply-status-effect.js';
import { removeStoredStatus } from '../ability/statuses/status-effect-removal-helpers.js';
import { boundaryAttempt, boundaryVerdict, creatureName, CREATURE_HIDDEN_STATUS, resolveCreatureVisibility, resolveGmBoundary, type GmBoundaryVerdict } from './gm-boundary.js';
import { statusItemId, statusRows, STATUS_IDS } from './expiry.js';
import { statusScalar } from '../actions/activity.js';
import { BURROWED_ITEM_ID } from '../movement/burrow-move.js';
import { SQUEEZED_ITEM_ID as SQUEEZED, VERY_SQUEEZED_ITEM_ID as VERY_SQUEEZED } from '../movement/squeeze-at-rest.js';
import { SPEED_TELEPORT_TYPE_ID } from '../movement/teleport-move.js';

const HUNKERED = 'BE6DC590-4EB7-B297-5AC6-8E47AA4E00EF';
const CLEAR_IDS: Readonly<Record<string, string>> = { grappled: STATUS_IDS.grappled, restrained: STATUS_IDS.restrained,
  prone: 'F5ACBDB4-D923-3684-57F6-FBF3F720AEFA' };

/** Each selected creature keeps its own connection; prior gains are never fired again for the next pick. */
export function resolveVisibilitySelection20(ctx: JanusCtx, targetIds: readonly string[], rollPrivate = 0): GmBoundaryVerdict {
  ctx.rollPrivate = rollPrivate;
  const ids = [...new Set(targetIds.map(upper))];
  if (ids.some(id => !ctx.members.has(id))) throw new HandBack('creature-visibility:target-not-held');
  const hidden = ids.some(id => !activeStatuses(ctx, ctx.members.get(id)!).some(status => statusItemId(status) === CREATURE_HIDDEN_STATUS));
  for (const id of ids) {
    ctx.statusConnectedId = null;
    if (hidden) resolveGmBoundary(ctx, { kind: 'add', targetId: id, casterId: id, itemId: CREATURE_HIDDEN_STATUS,
      durationInt: '', durationType: '', ends: { ends: '0-0-0-0-0-0', flags: [0, 0, 0, 0] }, originId: '', allowNoChange: true, rollPrivate });
    else resolveCreatureVisibility(ctx, id, false);
  }
  return boundaryVerdict(ctx);
}

export interface MovementStatusInputs20 {
  actor_id: string;
  updates: { x_pos: number; y_pos: number; z_pos: number };
  burrow?: { transition: 'enter' | 'leave' | 'none' };
  squeeze?: { squeezed: boolean; very: boolean };
  rest?: { hunkered: boolean; squeeze: { squeezed: boolean; very: boolean } | null };
  teleport?: { clears?: string[] };
  compound?: { members: Array<{ id?: string; hunkered?: boolean; after: { x_pos: number; y_pos: number; z_pos: number }; squeeze?: { squeezed: boolean; very: boolean } }> };
}

/** Status sources are held before the movement planner's first draw, through the shared browser adapter. */
export function movementStatusDefinitionIds20(command: MovementStatusInputs20, target?: Row, members?: ReadonlyMap<string, Row>): string[] {
  const held = new Set(statusRows(target ?? null, 'target').filter(status => Number(statusScalar(status, 'status_deleted') ?? 0) === 0).map(statusItemId));
  const ids = new Set<string>();
  const due = (id: string, desired: boolean): void => { if (desired || held.has(id)) ids.add(id); };
  if (command.burrow && command.burrow.transition !== 'none') due(BURROWED_ITEM_ID, command.burrow.transition === 'enter');
  const squeeze = command.squeeze ?? command.rest?.squeeze;
  if (squeeze) { due(SQUEEZED, squeeze.squeezed); due(VERY_SQUEEZED, squeeze.very); }
  if (command.rest?.hunkered) due(HUNKERED, false);
  for (const key of command.teleport?.clears ?? []) if (CLEAR_IDS[key]) due(CLEAR_IDS[key]!, false);
  for (const member of command.compound?.members ?? []) if (member.id && (member.squeeze || member.hunkered)) {
    for (const id of movementStatusDefinitionIds20({ actor_id: member.id, updates: member.after, squeeze: member.squeeze, ...(member.hunkered ? { rest: { hunkered: true, squeeze: null } } : {}) }, members?.get(upper(member.id)))) ids.add(id);
  }
  return [...ids];
}

function changeMovementStatus20(ctx: JanusCtx, actorId: string, itemId: string, desired: boolean, log: boolean,
  allTargetPeers = true, ownConnection = false): void {
  const actor = ctx.members.get(actorId);
  if (!actor) throw new HandBack('movement-status:actor-not-held');
  const held = activeStatuses(ctx, actor).filter(status => statusItemId(status) === itemId);
  if (desired === (held.length > 0)) return;
  boundaryAttempt(ctx, `Movement status for ${creatureName(ctx, actorId)}: ${String(itemField(ctx.read.definition(itemId), 'item_name') ?? 'condition')}`, (ctx) => {
    const target = ctx.members.get(actorId)!;
    const held = activeStatuses(ctx, target).filter(status => statusItemId(status) === itemId);
    if (desired) {
      applyStatusEffect(ctx, { casterId: actorId, targetIds: [actorId], abilityId: itemId, rawDiceMod: 0,
        rawDiceAdvDisStacks: 0, useAbilityVariable1: null, useAbilityVariable2: null, castingTargets: '' },
      {}, itemId, 'status_effect_applied', target, target, { ends: '0-0-0-0-0-0', flags: [0, 0, 0, 0] },
      ownConnection, log, 0, false, '');
    } else for (const status of held) {
      if (!activeStatuses(ctx, target).some(row => upper(row.status_id) === upper(status.status_id))) continue;
      removeStoredStatus(ctx, target, status, 'single', { allTargetPeers, skipConnectedPeerCull: ownConnection && !allTargetPeers });
      if (log) insertRoll(ctx, { roll_type_1: 'self_status_effect_deleted', roll_item_id: itemId, by: target });
    }
  });
}

/** Reconcile only this mover's canonical endpoint statuses, in the caller's existing effect frame. */
export function resolveMovementSqueeze20(ctx: JanusCtx, actorId: string, squeeze: { squeezed: boolean; very: boolean }): void {
  // Geometry conditions are independent of the action's connection. A downgrade also preserves a retained sibling
  // from older rows whose Squeezed and Very Squeezed shared one connection.
  changeMovementStatus20(ctx, actorId, SQUEEZED, squeeze.squeezed, false, !squeeze.very, true);
  changeMovementStatus20(ctx, actorId, VERY_SQUEEZED, squeeze.very, false, !squeeze.squeezed, true);
  for (const status of ctx.statusesAdded) if (status.target === actorId && (status.status_item_id === SQUEEZED || status.status_item_id === VERY_SQUEEZED))
    status.status_id ??= ctx.read.uuid();
}

/** The movement receiver saves this exact native verdict with the position and its existing receipt. */
export function resolveMovementStatusInputs20(ctx: JanusCtx, command: MovementStatusInputs20): GmBoundaryVerdict {
  const actor = ctx.members.get(upper(command.actor_id));
  if (!actor) throw new HandBack('movement-status:actor-not-held');
  Object.assign(ec(actor), command.updates);
  actor.position = { ...record(actor.position), ...command.updates };
  const actorId = ecId(actor);
  const change = (itemId: string, desired: boolean, log: boolean, allTargetPeers = true): void =>
    changeMovementStatus20(ctx, actorId, itemId, desired, log, allTargetPeers);
  if (command.burrow && command.burrow.transition !== 'none') change(BURROWED_ITEM_ID, command.burrow.transition === 'enter', true);
  const cleared: string[] = [];
  for (const key of command.teleport?.clears ?? []) {
    const itemId = CLEAR_IDS[key];
    if (!itemId || !activeStatuses(ctx, ctx.members.get(actorId)!).some(status => statusItemId(status) === itemId)) continue;
    change(itemId, false, false, false);
    if (!activeStatuses(ctx, ctx.members.get(actorId)!).some(status => statusItemId(status) === itemId)) cleared.push(itemId);
  }
  if (cleared.length) insertRoll(ctx, { roll_type_1: 'teleport_cleared_conditions', roll_type_2: cleared.join(','),
    roll_item_id: SPEED_TELEPORT_TYPE_ID, roll_result: cleared.length, by: actor });
  if (command.rest?.hunkered) change(HUNKERED, false, true);
  const squeeze = command.squeeze ?? command.rest?.squeeze;
  if (squeeze) resolveMovementSqueeze20(ctx, actorId, squeeze);
  for (const member of command.compound?.members ?? []) if (member.id && (member.squeeze || member.hunkered)) {
    const id = upper(member.id), follower = ctx.members.get(id);
    if (!follower) throw new HandBack('movement-status:follower-not-held');
    Object.assign(ec(follower), member.after);
    follower.position = { ...record(follower.position), ...member.after };
    if (member.hunkered) changeMovementStatus20(ctx, id, HUNKERED, false, true);
    if (member.squeeze) resolveMovementSqueeze20(ctx, id, member.squeeze);
  }
  for (const status of ctx.statusesAdded) status.status_id ??= ctx.read.uuid();
  return boundaryVerdict(ctx);
}
